import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Star, ArrowRight, Clock, Zap, Shield, Users, TrendingUp, FileText, Brain } from 'lucide-react';

const Pricing: React.FC = () => {
  const plans = [
    {
      name: "Streak Starter",
      price: "$0",
      period: "",
      description: "Perfect for getting started",
      popular: false,
      features: [
        "Dashboard (view-only tasks)",
        "1 Idea Validation per month",
        "Basic ROI Tracker",
        "Community access (read-only)"
      ],
      buttonText: "Start Free",
      buttonVariant: "outline" as const,
      theme: { border: 'border-slate-200', check: 'text-teal-600' }
    },
    {
      name: "Monthly Pro",
      price: "$5.99",
      period: "month",
      description: "Full access to everything",
      popular: true,
      features: [
        "Unlimited AI Idea Validation",
        "Full Dashboard & Task Management",
        "Founder Journal with AI insights",
        "Boss Mode accountability",
        "Advanced ROI Tracking",
        "Community Pods access",
        "Launch Playbooks & Templates",
        "Priority Support"
      ],
      buttonText: "Start Pro Monthly",
      buttonVariant: "default" as const,
      theme: { border: 'border-gradient-to-r from-yellow-400 to-orange-500', check: 'text-orange-600' }
    },
    {
      name: "Yearly Pro",
      price: "$59.99",
      period: "year",
      description: "2 months free ($4.99/month)",
      popular: false,
      features: [
        "Everything in Monthly Pro",
        "2 months free (save $12)",
        "Exclusive yearly masterclasses",
        "1-on-1 strategy session",
        "Advanced analytics dashboard",
        "Custom playbook creation",
        "Priority feature requests"
      ],
      buttonText: "Start Pro Yearly",
      buttonVariant: "default" as const,
      theme: { border: 'border-slate-200', check: 'text-purple-600' }
    }
  ];

  const features = [
    {
      icon: Brain,
      title: "AI Idea Validation",
      description: "Get brutally honest feedback from AI investors with probing questions and market analysis"
    },
    {
      icon: Clock,
      title: "30-Day Sprint Plan",
      description: "Personalized week-by-week breakdown with daily tasks and deadlines"
    },
    {
      icon: TrendingUp,
      title: "ROI Tracker",
      description: "Track expenses, revenue, and visualize your path to profitability"
    },
    {
      icon: FileText,
      title: "Launch Playbooks",
      description: "Proven templates for cold outreach, landing pages, and pricing strategies"
    },
    {
      icon: Users,
      title: "Community Pods",
      description: "Join small accountability groups for peer support and motivation"
    },
    {
      icon: Zap,
      title: "Boss Mode (Cashpurr)",
      description: "AI-powered daily accountability with task generation and deadline tracking"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white pt-32 pb-40">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
              Simple Pricing
              <br />
              <span className="font-bold">Start Building Today</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed">
              Choose your sprint plan. Cancel anytime. 
              Your first paying customer is 30 days away.
            </p>
            <div className="flex items-center justify-center gap-4 text-black-200">
              <Clock className="w-5 h-5" />
              <span>Free Plan</span>
              <span>•</span>
              <span>No credit card required</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`${plan.popular ? 'border-4' : 'border-2'} ${plan.theme.border} p-8 relative transition-all duration-300 hover:shadow-2xl ${plan.popular ? 'transform scale-105 shadow-2xl' : ''} ${plan.period === 'year' ? 'hover:border-purple-300' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-4 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className={`text-center pb-8 ${plan.popular ? 'bg-gradient-to-br from-yellow-50 to-orange-50' : ''}`}>
                  <Badge variant={plan.popular ? 'secondary' : 'outline'} className={`mx-auto mb-4 ${plan.popular ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black' : ''}`}>
                    {plan.period ? (plan.period === 'month' ? 'Monthly' : 'Yearly') : 'Free'}
                  </Badge>
                  <CardTitle className="text-2xl font-bold mb-4 text-slate-800">{plan.name}</CardTitle>
                  <div className="text-4xl font-bold">
                    {plan.popular ? (
                      <span className="bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">{plan.price}</span>
                    ) : (
                      <span className="text-slate-800">{plan.price}</span>
                    )}
                  </div>
                  {plan.period && <span className="text-gray-600 mt-1">/{plan.period}</span>}
                  <p className="text-slate-600 mt-2">{plan.description}</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3">
                        <CheckCircle className={`w-4 h-4 ${plan.theme.check} flex-shrink-0`} />
                        <span className="text-slate-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/signup" className="block">
                    <Button
                      variant={plan.buttonVariant}
                      className={`w-full text-base py-3 font-semibold ${
                        plan.popular
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black shadow-lg'
                          : plan.period === 'year'
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 text-white'
                            : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {plan.buttonText}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-light mb-8">What's Included</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every plan includes our complete toolkit for 30-day success
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center mb-6">
                  <feature.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-medium text-black mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed font-light">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-light mb-8">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {[
              {
                question: "Can I cancel my subscription anytime?",
                answer: "Yes, you can cancel your subscription at any time. No long-term contracts or hidden fees."
              },
              {
                question: "What happens after my 30-day sprint?",
                answer: "You can continue using the platform to track your business growth, or start a new sprint with a different idea."
              },
              {
                question: "Do you offer refunds?",
                answer: "We offer a 7-day money-back guarantee. If you're not satisfied, we'll refund your payment."
              },
              {
                question: "Can I switch between monthly and yearly plans?",
                answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect at your next billing cycle."
              },
              {
                question: "Is there a limit on AI validations?",
                answer: "No, you can validate unlimited ideas during your subscription period."
              }
            ].map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-medium text-black mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              Ready to Start Your Sprint?
            </h2>
            <p className="text-xl text-blue-200 mb-12 leading-relaxed">
              Join 500+ entrepreneurs who've already made their first sale
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Start Your Sprint Now
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-blue-200">
                <Shield className="w-5 h-5" />
                <span>Free Plan</span>
              </div>
            </div>
            
            <p className="text-blue-200 text-sm">
              No credit card required • Cancel anytime • 7-day money-back guarantee
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
